import React, {useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import axios from 'axios';
import ConfirmPopup from './ConfirmPopup.js';
import resizeImageFile from '../utils/resizeImageFile.js';

export default function FormImage({
	label = 'Obrázek',
	value = '',
	setValue,
	error = '',
	baseUrl = '/images/items/',
	uploadUrl = '/rest/uploadItemImage',
	uploadFieldName = 'ITEM_IMAGE',
	entityName = '',
	maxSize = 350,
	disabled = false
}) {
	const selectInputRef = useRef(null);
	const cameraInputRef = useRef(null);
	// obrázek nahraný v tomto rozpracovaném formuláři, který ještě není uložený v databázi
	const pendingUploadRef = useRef('');
	const [showSourcePopup, setShowSourcePopup] = useState(false);
	const [uploading, setUploading] = useState(false);
	const [message, setMessage] = useState('');
	const [imageLoadError, setImageLoadError] = useState(false);

	const imageUrl = value !== '' ? `${baseUrl}${value}` : '';
	const showImage = imageUrl !== '' && !imageLoadError;

	const openSourcePopup = () => {
		if (disabled || uploading) {
			return;
		}

		setMessage('');
		setShowSourcePopup(true);
	};

	const closeSourcePopup = () => {
		setShowSourcePopup(false);
	};

	const handleRemove = () => {
		if (disabled || uploading) {
			return;
		}

		setMessage('');
		setImageLoadError(false);
		setValue('');
	};

	const handleImageSelected = async (e) => {
		const file = e.target.files?.[0];
		e.target.value = '';

		if (!file) {
			return;
		}

		setShowSourcePopup(false);

		if (!file.type.startsWith('image/')) {
			setMessage('Vyber obrázek ve formátu PNG, JPG nebo WEBP.');
			return;
		}

		setUploading(true);
		setMessage('');

		try {
			const resized = await resizeImageFile(file, maxSize);

			const formData = new FormData();
			formData.append(uploadFieldName, resized.blob, `upload.${resized.extension}`);
			formData.append('NAME', entityName || '');
			formData.append('REPLACE_IMG', pendingUploadRef.current);

			const response = await axios({
				method: 'POST',
				url: uploadUrl,
				data: formData,
				withCredentials: true,
				headers: {
					'Content-Type': 'multipart/form-data',
					'Authorization': `Bearer ${localStorage.getItem('token') || ''}`
				}
			});

			const uploadedName = response.data?.data?.IMG || '';

			if (uploadedName === '') {
				setMessage(response.data?.message || 'Upload obrázku se nepodařil.');
				return;
			}

			pendingUploadRef.current = uploadedName;
			setImageLoadError(false);
			setValue(uploadedName);
		} catch (uploadError) {
			setMessage(uploadError?.response?.data?.message || uploadError.message || 'Upload obrázku se nepodařil.');
		} finally {
			setUploading(false);
		}
	};

	const renderPopup = (popupNode) => {
		if (typeof document === 'undefined') {
			return popupNode;
		}

		return createPortal(popupNode, document.body);
	};

	return (
		<div className={`input-div${error !== '' ? ' has-error' : ''}`}>
			{label !== '' || error !== '' ? (
				<div className="input-meta">
					<div className="input-meta-left">{label !== '' ? <div className="input-label">{label}</div> : ''}</div>
					{error !== '' ? <div className="input-error">{error}</div> : ''}
				</div>
			) : ''}

			<div className="input-control">
				<div className="input-image">
					<button type="button" className="input-image_preview" onClick={openSourcePopup} disabled={disabled}>
						{showImage ? (
							<img className="input-image_preview_img" src={imageUrl} alt="" onError={() => setImageLoadError(true)} />
						) : (
							<span className="input-image_preview_empty">bez obrázku</span>
						)}
					</button>

					<div className="input-image_side">
						<div className="input-image_actions">
							<button type="button" className="button small" onClick={openSourcePopup} disabled={disabled || uploading}>
								{uploading ? 'Nahrávám...' : 'Nahrát'}
							</button>
							{value !== '' ? (
								<button type="button" className="button small red" onClick={handleRemove} disabled={disabled || uploading}>
									Odebrat
								</button>
							) : ''}
						</div>

						<div className="input-image_name">{value !== '' ? value : 'Žádný obrázek'}</div>
					</div>
				</div>

				{message !== '' ? <div className="input-image_message">{message}</div> : ''}
			</div>

			<input ref={selectInputRef} type="file" accept="image/*" className="input-image_file" onChange={handleImageSelected} />
			<input ref={cameraInputRef} type="file" accept="image/*" capture="environment" className="input-image_file" onChange={handleImageSelected} />

			{renderPopup(
				<ConfirmPopup showConfirmPopup={showSourcePopup} closeConfirmPopup={closeSourcePopup} infoHeader={label} infoMain="Vyber zdroj obrázku." popupContentClassName="usrprofile_popup_content">
					<div className="usrprofile_popup_actions">
						<button type="button" className="button mr_1" onClick={() => selectInputRef.current?.click()}>Vybrat obrázek</button>
						<button type="button" className="button" onClick={() => cameraInputRef.current?.click()}>Vyfotit</button>
					</div>
				</ConfirmPopup>
			)}
		</div>
	);
}
